<template>
    <div class="report-modal">
        <div class="report-modal__title">Написать в техподдержку</div>

        <div class="report-modal__form">
            <UISelect
                id="category"
                v-model="form.category"
                class="report-modal__input"
                :options="categoryOptions"
                label="Категория обращения"
                :error-message="errors.category?.join() ?? ''"
            />

            <UITextArea
                id="message"
                v-model="form.message"
                class="report-modal__textarea"
                label="Текст обращения"
                :height="null"
                :max-length="1100"
                :resizable="false"
                :error-message="errors.message?.join() ?? ''"
            />

            <UIMediaList
                :allowed-file-types="[
                    'image/*',
                    'application/pdf',
                    'application/msword',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ]"
                :max-files="CarMaxDocuments"
                @media-updated="onUpdatedFiles"
            />

            <UISimpleButton
                class="report-modal__button"
                :loading="isSendLoading"
                @clicked="onSubmit"
            >
                Отправить
            </UISimpleButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { ModalKey } from '@/injection/keys.ts';
    import { ModalPluginType } from '@/plugins/modalPlugin.ts';

    import { MediaItem, SelectOption } from '@/types/form.type.ts';
    import { FeedbackForm, FeedbackFormErrors, getFeedbackCategorySelectOptions } from '@/types/feedback';
    import { CarMaxDocuments } from '@/types/car/car-constants.type.ts';

    import useFeedbackRepository from '@/repositories/feedback/useFeedbackRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useNotifications from '@/utils/useNotifications.ts';

    import UIMediaList from '@/components/ui/UIMediaList/UIMediaList.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UITextArea from '@/components/ui/UITextArea/UITextArea.vue';

    const $modal = injectStrict<ModalPluginType>(ModalKey);

    const { sendFeedback } = useFeedbackRepository();
    const { showSuccess } = useNotifications();
    const { isLoading: isSendLoading, setLoading: setSendLoading } = useLoading(false);

    const form = ref<FeedbackForm>({
        category: undefined,
        message: '',
        files: [],
    });
    const errors = ref<FeedbackFormErrors>({});
    const categoryOptions = ref<SelectOption[]>(getFeedbackCategorySelectOptions());

    function onSubmit(): void {
        setSendLoading(true);
        errors.value = {};

        sendFeedback(mapFeedbackFormToRequestForm())
            .then(() => {
                showSuccess({
                    title: 'Обращение успешно отправлено!',
                });

                $modal.close();
            })
            .catch((formErrors: FeedbackFormErrors) => {
                errors.value = formErrors;
            })
            .finally(() => {
                setSendLoading(false);
            });
    }

    function onUpdatedFiles(updatedMedia: MediaItem[]): void {
        form.value.files = [];

        updatedMedia.forEach((media: MediaItem) => {
            if (media.file !== null) {
                form.value.files?.push(media.file);
            }
        });
    }

    function mapFeedbackFormToRequestForm(): FeedbackForm {
        const requestForm: FeedbackForm = {};

        if (form.value.category !== undefined) {
            requestForm.category = form.value.category;
        }

        if (form.value.message !== undefined && form.value.message.length > 0) {
            requestForm.message = form.value.message;
        }

        if (form.value.files !== undefined && form.value.files.length > 0) {
            requestForm.files = [...form.value.files];
        }

        return requestForm;
    }
</script>

<style scoped lang="scss">
    @use 'ReportModal' as *;
</style>
